import { FunctionComponent, useContext, useState } from "react";
import { DataImportResult, OrderDataImportDraft } from "../../../../interfaces/common/data-importing";
import { CustomerType } from "../../../../interfaces/common/customer";
import GenericTable from "../../../common/generic-table/generic-table";
import { displayLocation, displayMultipleLocations } from "../../../../utils/locations";
import CustomDialog from "../../../common/custom-dialog/custom-dialog";
import { DialogContent, DialogTitle, Typography, Chip, Button, CircularProgress, Alert } from "@mui/material";
import { isMobile, isTablet } from "react-device-detect";
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { AuthContext } from "../../../../contexts/user-context/user-context";
import axios, { AxiosResponse, isAxiosError } from "axios";
import { API_ENDPOINT } from "../../../../configurations/global.config";
import { useSnackBar } from "../../../../contexts/snackbar";
import { ErrorsFlattenner } from "../../../common/errors-flattenner/errors-flattenner";
import { get_cod } from "../../../../utils/cod";
import { OrderType } from "../../../../interfaces/delivery-company/orders";
import { displayMoney } from "../../../../utils/money";
import { showWorkflow } from "../../../common/styles/workflow-display";
import OrderNotes from "../../../common/order-notes/order-notes";

interface PreviewOrdersProps {
    data: DataImportResult<OrderDataImportDraft>;
    onClose: () => void;
    onSave: () => void;
}

const WillBeCreatedChip = ()=> <Chip
                                icon={<AddCircleOutlineIcon />}
                                label="Will be Created"
                                color="success"
                                variant="filled"
                                size="small"
                                sx={{m:1}}
                            />
                                
const WillReuseExisting = () => <Chip
                            icon={<ErrorOutlineIcon />}
                            label="Already Exists"
                            color="warning"
                            variant="filled"
                            size="small"
                            sx={{m:1}}
                        />
const PreviewOrders: FunctionComponent<PreviewOrdersProps> = ({ data, onClose, onSave }) => {
    const [isSaving, setIsSaving] = useState(false)
    const {config} = useContext(AuthContext)
    const [response, setResponse] = useState<AxiosResponse>()
    const { showMessage } = useSnackBar()
    
    const handleSave = async () => {
        setIsSaving(true)
        setResponse(undefined)
        try {
            const endpoint = `${API_ENDPOINT}/v2/orders/import/save/`
            await axios.post(endpoint, data.draft.map(item => {return {...item, merchant_location: item.merchant_location?.id ?? 0, cod: get_cod(item)}}), config)
            showMessage("Records saved successfully.")
            onSave()
        } catch(e) {
            if (isAxiosError(e)) {
                const response = e.response
                if (response?.status == 403) {
                    showMessage("Your account is blocked by the delivery company", 'error')
                } else {
                    showMessage("Failed to save records.", 'error')
                }

                
            } else {
                showMessage("Failed to save records.", 'error')
            }
        } finally {
            setIsSaving(false)
        }

    }

    const nothingToSave = data.draft.filter(item => item._op == 'create').length == 0
    return (
        <CustomDialog open={true} onClose={onClose} maxWidth={"xl"} fullWidth fullScreen={(isMobile && !isTablet)}>
            <DialogTitle>
                <Typography align="center" variant="h4" component={"h4"}>
                    Import Preview
                </Typography>
            </DialogTitle>
            <DialogContent>
                <div style={{display: "flex", justifyContent: "center", gap:4}}>
                    <Button variant="outlined" disabled={isSaving || nothingToSave} onClick={handleSave}>Save {isSaving && <CircularProgress size={20} sx={{ml:2}}/>}</Button>
                    <Button  disabled={isSaving} onClick={onClose} variant="outlined" color="error">Cancel</Button>
                </div>

                <Alert sx={{mt: 2, mb: 2}} severity="info">
                    Items marked with <b>Already Exists</b> already exist in your account and will be reused.
                </Alert>
                
                {
                    nothingToSave && <Alert sx={{mt: 2, mb: 2}} severity="warning">
                    No new records were found. Nothing to save.
                </Alert>
                }

                {
                    response && <><Alert
                                severity={
                                  response && response.status >= 200 && response.status < 300
                                    ? 'success'
                                    : 'error'
                                }
                              >
                                    <ErrorsFlattenner rawErrors={response.data} />
                              </Alert>
                              <br />
                            </>
                }
                <GenericTable<OrderDataImportDraft>
                    sourceView="merchant-orders-bulk-import-preview"
                    items={data.draft}
                    selectedItems={new Map()}
                    isLoading={false}
                    fullWidth
                    columns={[
                        {
                            id: "reference",
                            header: "Reference ID",
                            display_body: (item) => item.reference_id
                        },
                        {
                            id: "customer",
                            header: "Customer",
                            display_body: (item) => {
                                if (!item.customer) return <Alert severity="error">ERROR. Contact Support</Alert>
                                return <>
                                    {item.customer.name}
                                    <br />
                                    {item.customer.phone_number}
                                    <br />
                                    {item.customer._op == "create" ? <WillBeCreatedChip /> : <WillReuseExisting />} 
                                </>
                            }
                        },
                        {
                            id: "customer_location",
                            header: "Customer Location", 
                            display_body: (item) => {
                                if (!item.customer_location) return <Alert severity="error">ERROR. Contact Support</Alert>
                                return <>
                                    {displayLocation(item.customer_location)}
                                    <br />
                                    {item.customer_location._op == "create" ? <WillBeCreatedChip /> : <WillReuseExisting />} 
                                </>
                            }
                        },
                        {
                            id: "merchant_location",
                            header: "Merchant Location",
                            display_body: (item) => {
                                if (!item.merchant_location) return <Alert severity="error">ERROR. Contact Support</Alert>
                                return <>
                                    {displayLocation(item.merchant_location)}
                                </>
                            }
                        },
                        {
                            id: "cod",
                            header: "COD",
                            display_body: (item) => displayMoney(get_cod(item))
                        },
                        {
                            id: "workflow",
                            header: "Workflow",
                            display_body: (item) => showWorkflow(item.workflow, item.is_exchange, item.exchange_for, item.return_reason)
                        },
                        {
                            id: "description",
                            header: "Description",
                            display_body: (item) => item.description,
                        },
                        {
                            id: "notes",
                            header: "Notes",
                            display_body: (item) => <OrderNotes order={item} /> 
                        }
                    ]}
                    getItemUID={(item) => `${item._op}${item.reference_id}${item}`}
                />
            </DialogContent>
        </CustomDialog>
    );
};

export default PreviewOrders;
